import { CognitoIdentityServiceProvider } from 'aws-sdk'
import fetch from 'node-fetch'

interface PasswordPolicyTrait {
    name: string;
    useQty: boolean;
  }

const policyBuilder: { [index: string]: { [index: string]: PasswordPolicyTrait }} = {
  'must be at least': {
    MinimumLength: {
      name: 'characters',
      useQty: true
    }
  },
  'requires at least': {
    RequireUppercase: {
      name: 'uppercase',
      useQty: false
    },
    RequireLowercase: {
      name: 'lowercase',
      useQty: false
    },
    RequireNumbers: {
      name: 'number',
      useQty: false
    },
    RequireSymbols: {
      name: 'symbol',
      useQty: false
    }
  }
}

export const BuildPolicySentence = (policy: CognitoIdentityServiceProvider.PasswordPolicyType): string => {
  const sentenceParts = ['Password']
  const policyCopy: { [index: string]: (number | boolean) } = JSON.parse(JSON.stringify(policy))
  for (const key of Object.keys(policyCopy)) {
    if (!policyCopy[key]) {
      delete policyCopy[key]
    }
  }
  delete policyCopy.TemporaryPasswordValidityDays
  let sentenceCount = 0
  const policyKeys = Object.keys(policyCopy)
  const policyBuilderKeys = Object.keys(policyBuilder)
  for (const sentencePart of policyBuilderKeys) {
    const relevantPolicyKeys = policyKeys.filter(k => policyBuilder[sentencePart][k])
    const hasRelevantKeys = relevantPolicyKeys.length > 0
    if (hasRelevantKeys) { sentenceParts.push(sentencePart) }
    let partCount = 0
    for (const policyKey of relevantPolicyKeys) {
      let needsComma = false
      const part = policyBuilder[sentencePart][policyKey]
      if (relevantPolicyKeys.length > 2 && partCount + 1 < relevantPolicyKeys.length) { needsComma = true }
      if (relevantPolicyKeys.length > 1 && partCount + 1 === relevantPolicyKeys.length) { sentenceParts.push('and') }
      if (part.useQty) { sentenceParts.push(`${policyCopy[policyKey]}`) } else { sentenceParts.push('one') }
      sentenceParts.push(part.name)
      if (needsComma) { sentenceParts[sentenceParts.length - 1] = `${sentenceParts[sentenceParts.length - 1]},` }
      partCount++
    }
    if (hasRelevantKeys) {
      if (sentenceCount + 1 < policyBuilderKeys.length) { sentenceParts.push('and') }
    }
    sentenceCount++
  }
  sentenceParts[sentenceParts.length - 1] = `${sentenceParts[sentenceParts.length - 1]}.`
  return sentenceParts.join(' ')
}

export const GetPasswordPolicy = async (apiUrl: string): Promise<CognitoIdentityServiceProvider.PasswordPolicyType> => {
  const results = await fetch(`${apiUrl}/api/password-policy`, {
    method: 'GET'
  })
  return results.json()
}

export class PasswordResetError extends String {}

export const ConformToPolicy = (
  policy: AWS.CognitoIdentityServiceProvider.PasswordPolicyType,
  password: string,
  passwordConfirm: string): void => {
  // special char list from:
  // https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-policies.html#user-pool-settings-password-policies
  const specialChars = [
    '=', '+', '-', '^', '$', '*', '.', '[', ']', '{', '}', '(', ')', '?', '"', '!',
    '@', '#', '%', '&', '/', '\\', ',', '>', '<', '\'', ':', ';', '|', '_', '~', '`'
  ]
  if (password !== passwordConfirm) {
    throw new PasswordResetError('Passwords do not match.')
  }
  if (policy.MinimumLength && password.length < policy.MinimumLength) {
    throw new PasswordResetError('Password does not meet minimum length requirements.')
  }
  if (policy.RequireSymbols && !specialChars.some(c => password.includes(c))) {
    throw new PasswordResetError('One special character is required.')
  }
  if (policy.RequireUppercase && !(new RegExp('[A-Z]').test(password))) {
    throw new PasswordResetError('One uppercase is required.')
  }
  if (policy.RequireLowercase && !(new RegExp('[a-z]').test(password))) {
    throw new PasswordResetError('One lowercase is required.')
  }
  if (policy.RequireNumbers && !(new RegExp('[0-9]').test(password))) {
    throw new PasswordResetError('One number is required.')
  }
}

export const EmailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
